import React from "react";
import {  getInitials } from "../Meetings/Meetings.utils";
import { buildAuthorMap, getAvatarColor, resolveAuthor, resolveMentions } from "./Conversation.utils";

function ConversationMessages({ group, isLoading }) {
  if (!group) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-body-secondary">
        <i className="bi bi-chat-dots fs-1 mb-3" />
        <h6>Select a conversation</h6>
        <small>Choose a channel from the sidebar to view messages</small>
      </div>
    );
  }

  const authorMap = buildAuthorMap(group.messages);

  return (
    <div className="d-flex flex-column flex-grow-1 overflow-hidden">
      <div className="px-4 py-3 border-bottom flex-shrink-0">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h6 className="fw-bold mb-0">
              {group.isChannel ? `#${group.label}` : group.label}
            </h6>
            <small className="text-body-secondary">{group.platform}</small>
          </div>
          <span className="badge bg-primary-subtle text-primary-emphasis rounded-pill px-3 py-2">
            <i className="bi bi-chat-left-text me-1" />
            {group.messages.length} messages
          </span>
        </div>
      </div>

      <div className="flex-grow-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="text-center text-body-secondary py-5">
            <div className="spinner-border spinner-border-sm me-2" />
            Loading messages...
          </div>
        ) : group.messages.length === 0 ? (
          <div className="text-center text-body-secondary py-5">
            <i className="bi bi-chat-slash fs-2 d-block mb-2" />
            No messages in this conversation
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {group.messages.map((msg) => {
              const authorName = resolveAuthor(msg.author, authorMap);
              const hasThreads = msg.threads?.some((t) => t.text?.trim());

              return (
                <div
                  key={msg.uuid || msg._id}
                  className="d-flex align-items-start gap-3"
                >
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0 text-white"
                    style={{
                      width: 38,
                      height: 38,
                      fontSize: 12,
                      backgroundColor: getAvatarColor(authorName),
                    }}
                  >
                    {getInitials(authorName)}
                  </div>

                  <div className="flex-grow-1 min-w-0">
                    {/* Author + timestamp */}
                    <div className="d-flex align-items-baseline gap-2 mb-1">
                      <span className="fw-semibold" style={{ fontSize: 13 }}>
                        {authorName}
                      </span>
                      {msg.sentAt && (
                        <small
                          className="text-body-secondary"
                          style={{ fontSize: 11 }}
                        >
                          {new Date(msg.sentAt).toLocaleString()}
                        </small>
                      )}
                    </div>

                    <div
                      className="border rounded-3 px-3 py-2 bg-body-tertiary d-inline-block"
                      style={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        maxWidth: "75%",
                        wordBreak: "break-word",
                      }}
                    >
                      {resolveMentions(msg.text, authorMap)}
                    </div>

                    {hasThreads && (
                      <div className="mt-2 ms-1 ps-3 border-start border-2">
                        <small
                          className="text-primary fw-semibold d-block mb-2"
                          style={{ fontSize: 11 }}
                        >
                          <i className="bi bi-chat-dots me-1" />
                          {msg.threads.filter((t) => t.text?.trim()).length}{" "}
                          {msg.threads.filter((t) => t.text?.trim()).length === 1
                            ? "reply"
                            : "replies"}
                        </small>
                        <div className="d-flex flex-column gap-2">
                          {msg.threads
                            .filter((t) => t.text?.trim())
                            .map((thread, ti) => {
                              const tName = resolveAuthor(
                                thread.author,
                                authorMap,
                              );
                              return (
                                <div
                                  key={ti}
                                  className="d-flex align-items-start gap-2"
                                >
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0 text-white"
                                    style={{
                                      width: 26,
                                      height: 26,
                                      fontSize: 10,
                                      backgroundColor: getAvatarColor(tName),
                                    }}
                                  >
                                    {getInitials(tName)}
                                  </div>

                                  <div>
                                    <div className="d-flex align-items-baseline gap-2 mb-1">
                                      <span
                                        className="fw-semibold"
                                        style={{ fontSize: 12 }}
                                      >
                                        {tName}
                                      </span>
                                      {thread.sentAt && (
                                        <small
                                          className="text-body-secondary"
                                          style={{ fontSize: 10 }}
                                        >
                                          {new Date(
                                            thread.sentAt,
                                          ).toLocaleString()}
                                        </small>
                                      )}
                                    </div>

                                    <div
                                      className="border rounded-3 px-3 py-2 d-inline-block"
                                      style={{
                                        fontSize: 12,
                                        lineHeight: 1.5,
                                        whiteSpace: "pre-wrap",
                                        maxWidth: "100%",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {resolveMentions(thread.text, authorMap)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationMessages;